import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { Box, IconButton, Step, StepLabel, Stepper, useMediaQuery, useTheme } from '@mui/material';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { centered } from '@ui/style/style';
const STEP_ICON_SIZE = 36;
const STEP_CONNECTOR_GAP = 20;
const VISIBLE_STEPS = 4;
const SCROLL_EPSILON = 2;
const getMaxScrollLeft = (container) => Math.max(0, container.scrollWidth - container.clientWidth);
const readScrollState = (container) => {
    const maxScroll = getMaxScrollLeft(container);
    return {
        canScrollLeft: container.scrollLeft > SCROLL_EPSILON,
        canScrollRight: container.scrollLeft < maxScroll - SCROLL_EPSILON
    };
};
const CustomStepIcon = (props) => {
    const { active, completed, icon } = props;
    const isHighlighted = completed || active;
    return (_jsx(Box, { sx: {
            ...centered,
            width: STEP_ICON_SIZE,
            height: STEP_ICON_SIZE,
            borderRadius: '50%',
            border: '1px solid',
            borderColor: isHighlighted ? '#00B6ED' : '#DFE3E8',
            backgroundColor: isHighlighted ? '#00B6ED' : '#FFFFFF',
            color: isHighlighted ? '#FFFFFF' : '#919EAB',
            fontWeight: 500,
            fontSize: 16,
            fontFamily: 'SuisseIntl, sans-serif',
            zIndex: 1
        }, children: completed ? _jsx(Icon, { icon: 'eva:checkmark-fill', width: 20, height: 20 }) : icon }));
};
const stepLabelSx = {
    '& .MuiStepLabel-label': {
        mt: 1,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.3,
        color: (theme) => theme.palette.grey[600],
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
        px: 0.5
    },
    '& .MuiStepLabel-label.Mui-active': {
        color: (theme) => theme.palette.text.primary,
        fontWeight: 500
    },
    '& .MuiStepLabel-label.Mui-completed': {
        color: (theme) => theme.palette.primary.main,
        fontWeight: 500
    }
};
const scrollButtonSx = {
    flexShrink: 0,
    alignSelf: 'flex-start',
    mt: 0.25,
    width: 32,
    height: 32,
    border: '1px solid #DFE3E8',
    borderRadius: 1,
    color: '#637381',
    '&:hover': {
        backgroundColor: '#F4F6F8',
        borderColor: '#C4CDD5'
    },
    '&.Mui-disabled': {
        opacity: 0.4,
        borderColor: '#DFE3E8'
    }
};
const EventRegistrationStepper = ({ steps, activeStep, hideLabels }) => {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.down('md'));
    const shouldHideLabels = hideLabels ?? isMd;
    const scrollRef = useRef(null);
    const stepRefs = useRef([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const showNavigation = steps.length > VISIBLE_STEPS;
    const updateScrollState = useCallback(() => {
        const container = scrollRef.current;
        if (!container)
            return;
        const { canScrollLeft: nextCanScrollLeft, canScrollRight: nextCanScrollRight } = readScrollState(container);
        setCanScrollLeft(nextCanScrollLeft);
        setCanScrollRight(nextCanScrollRight);
    }, []);
    const scrollActiveStepIntoView = useCallback(() => {
        const container = scrollRef.current;
        const stepEl = stepRefs.current[activeStep];
        if (!container || !stepEl)
            return;
        const containerRect = container.getBoundingClientRect();
        const stepRect = stepEl.getBoundingClientRect();
        const stepLeft = stepRect.left - containerRect.left + container.scrollLeft;
        const stepRight = stepLeft + stepRect.width;
        const viewLeft = container.scrollLeft;
        const viewRight = viewLeft + container.clientWidth;
        if (stepLeft < viewLeft) {
            container.scrollTo({ left: stepLeft, behavior: 'smooth' });
            return;
        }
        if (stepRight > viewRight) {
            container.scrollTo({ left: stepRight - container.clientWidth, behavior: 'smooth' });
        }
    }, [activeStep]);
    const scrollByStep = useCallback((direction) => {
        const container = scrollRef.current;
        if (!container)
            return;
        const stepWidth = container.clientWidth / VISIBLE_STEPS;
        const maxScroll = getMaxScrollLeft(container);
        const nextScrollLeft = Math.min(maxScroll, Math.max(0, container.scrollLeft + (direction === 'left' ? -stepWidth : stepWidth)));
        container.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
        const syncScrollState = () => updateScrollState();
        container.addEventListener('scrollend', syncScrollState, { once: true });
        window.setTimeout(syncScrollState, 300);
    }, [updateScrollState]);
    useEffect(() => {
        updateScrollState();
    }, [steps.length, updateScrollState]);
    useEffect(() => {
        scrollActiveStepIntoView();
    }, [activeStep, scrollActiveStepIntoView]);
    useEffect(() => {
        const container = scrollRef.current;
        if (!container)
            return;
        const handleScroll = () => updateScrollState();
        container.addEventListener('scroll', handleScroll, { passive: true });
        const resizeObserver = new ResizeObserver(() => {
            updateScrollState();
        });
        resizeObserver.observe(container);
        return () => {
            container.removeEventListener('scroll', handleScroll);
            resizeObserver.disconnect();
        };
    }, [updateScrollState]);
    if (!steps.length)
        return null;
    const connectorLineSx = (stepIndex) => ({
        '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            top: STEP_ICON_SIZE / 2,
            left: `calc(50% + ${STEP_ICON_SIZE / 2 + STEP_CONNECTOR_GAP}px)`,
            width: `calc(100% - ${STEP_ICON_SIZE + STEP_CONNECTOR_GAP * 2}px)`,
            height: 2,
            borderRadius: 999,
            backgroundColor: stepIndex < activeStep ? '#00B6ED' : '#DFE3E8',
            transform: 'translateY(-50%)',
            zIndex: 0,
            pointerEvents: 'none'
        }
    });
    const stepperWidthPercent = Math.max(1, steps.length / VISIBLE_STEPS) * 100;
    return (_jsx(Box, { mb: shouldHideLabels ? 1 : 2, width: '100%', sx: { mx: 'auto' }, children: _jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                width: '100%'
            }, children: [showNavigation && (_jsx(IconButton, { size: 'small', onClick: () => scrollByStep('left'), disabled: !canScrollLeft, "aria-label": 'Previous steps', sx: scrollButtonSx, children: _jsx(ArrowLeft2, { size: 16 }) })), _jsx(Box, { ref: scrollRef, sx: {
                        flex: 1,
                        minWidth: 0,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        scrollBehavior: 'smooth',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        }
                    }, children: _jsx(Stepper, { activeStep: activeStep, orientation: 'horizontal', alternativeLabel: true, connector: null, sx: {
                            width: `${stepperWidthPercent}%`,
                            mx: 0,
                            backgroundColor: 'transparent',
                            '& .MuiStep-root': {
                                padding: 0,
                                flex: `0 0 ${100 / steps.length}%`,
                                minWidth: 0,
                                position: 'relative'
                            },
                            '& .MuiStepLabel-root': {
                                width: '100%'
                            },
                            '& .MuiStepLabel-iconContainer': {
                                position: 'relative',
                                zIndex: 1,
                                paddingRight: 0
                            },
                            '& .MuiStepLabel-alternativeLabel': {
                                flexDirection: 'column',
                                alignItems: 'center'
                            }
                        }, children: steps.map((step, index) => (_jsx(Step, { ref: (el) => {
                                stepRefs.current[index] = el;
                            }, sx: connectorLineSx(index), children: _jsx(StepLabel, { StepIconComponent: CustomStepIcon, sx: stepLabelSx, children: !shouldHideLabels ? step.label : undefined }) }, `${step.label}-${index}`))) }) }), showNavigation && (_jsx(IconButton, { size: 'small', onClick: () => scrollByStep('right'), disabled: !canScrollRight, "aria-label": 'Next steps', sx: scrollButtonSx, children: _jsx(ArrowRight2, { size: 16 }) }))] }) }));
};
export default EventRegistrationStepper;
